import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { fetchCourseCategories } from "@/api/fetchCourseCategories/fetchCourseCategoriesApi";
import { updateCourseCategory } from "@/api/updateCourseCategory/updateCourseCategoryApi";
import type { FetchCourseCategoriesResponse } from "@/api/fetchCourseCategories/types";
import type {
  UpdateCourseCategoryPayload,
  UpdateCourseCategoryResponse,
} from "@/api/updateCourseCategory/types";
import type { CourseCategory } from "@/types/learningCenter";

/**
 * Category Management State
 *
 * Manages the hierarchical list of course categories with CRUD operations
 */
export interface CategoryManagementState {
  categories: CourseCategory[];
  flatCategories: CourseCategory[]; // Flattened list for easier searching
  totalCount: number;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: CategoryManagementState = {
  categories: [],
  flatCategories: [],
  totalCount: 0,
  loading: false,
  error: null,
};

/**
 * Helper function to flatten hierarchical categories
 */
const flattenCategories = (categories: CourseCategory[]): CourseCategory[] => {
  const flat: CourseCategory[] = [];
  const flatten = (cats: CourseCategory[]) => {
    cats.forEach((cat) => {
      flat.push(cat);
      if (cat.children && cat.children.length > 0) {
        flatten(cat.children);
      }
    });
  };
  flatten(categories);
  return flat;
};

/**
 * Async thunk: Fetch course categories
 */
export const fetchCourseCategoriesThunk = createAsyncThunk<
  FetchCourseCategoriesResponse,
  void,
  { rejectValue: string }
>("categoryManagement/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchCourseCategories();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch course categories",
    );
  }
});

/**
 * Async thunk: Update a course category
 */
export const updateCourseCategoryThunk = createAsyncThunk<
  UpdateCourseCategoryResponse,
  { id: number; payload: UpdateCourseCategoryPayload },
  { rejectValue: string }
>(
  "categoryManagement/updateCategory",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateCourseCategory(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course category",
      );
    }
  },
);

/**
 * Category Management Slice
 */
const categoryManagementSlice = createSlice({
  name: "categoryManagement",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch categories
    builder
      .addCase(fetchCourseCategoriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseCategoriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.categories;
        state.flatCategories = flattenCategories(
          action.payload.data.categories,
        );
        state.totalCount =
          action.payload.meta?.totalCount || state.flatCategories.length;
      })
      .addCase(fetchCourseCategoriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch course categories";
      });

    // Update category
    builder
      .addCase(updateCourseCategoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourseCategoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update the category in the flat list
        const index = state.flatCategories.findIndex(
          (cat) => cat.id === action.payload.data.id,
        );
        if (index !== -1) {
          state.flatCategories[index] = action.payload.data;
        }
        // Note: The hierarchical structure will be refreshed on next fetch
      })
      .addCase(updateCourseCategoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update course category";
      });
  },
});

// Actions
export const { clearError } = categoryManagementSlice.actions;

// Selectors
export const selectCategories = (state: RootState) =>
  state.categoryManagement?.categories || [];
export const selectFlatCategories = (state: RootState) =>
  state.categoryManagement?.flatCategories || [];
export const selectTotalCount = (state: RootState) =>
  state.categoryManagement?.totalCount || 0;
export const selectLoading = (state: RootState) =>
  state.categoryManagement?.loading || false;
export const selectError = (state: RootState) =>
  state.categoryManagement?.error || null;

// Reducer
export default categoryManagementSlice.reducer;
